import { Capacitor, CapacitorHttp } from '@capacitor/core'
import type { HttpHeaders, HttpOptions } from '@capacitor/core'

import { FixtureSyncGame } from './fixtureSync'

type GraphQlBody = {
	operationName: string
	query: string
	variables?: Record<string, unknown>
}

type GraphQlEnvelope<T> = {
	data?: T
	errors?: unknown
}

export type PlayHqOrganisationSearchResult = {
	id: string
	routingCode: string
	name: string
	type: string
	tenantSlug?: string
	logoUrl?: string
}

export type PlayHqSeasonOption = {
	competitionId: string
	compName: string
	logoUrl?: string
	orgName: string
	organisationCode: string
	organisationId?: string
	seasonId: string
	seasonName: string
	status?: string
	startDate?: string
	endDate?: string
	tenant: string
	tenantSlug?: string
}

export type PlayHqTeamOption = PlayHqSeasonOption & {
	id: string
	teamName: string
	gradeId: string
	gradeName: string
	ageGroupName?: string
	genderName?: string
}

export type PlayHqFixtureGame = FixtureSyncGame & {
	amHome: boolean
	status?: string
	meScore?: number | null
	themScore?: number | null
	mePeriodScores?: number[]
	themPeriodScores?: number[]
}

export type PlayHqGameScores = {
	status?: string
	meScore?: number | null
	themScore?: number | null
	mePeriodScores?: number[]
	themPeriodScores?: number[]
}

const PLAYHQ_ORIGIN = 'https://www.playhq.com'
const SEARCH_ENDPOINT = 'https://search.playhq.com/graphql'
const API_ENDPOINT = 'https://api.playhq.com/graphql'
const SPECTATOR_ENDPOINT = 'https://spectator.playhq.com/graphql'
const DEFAULT_TENANT = 'na'

const USER_AGENT = [
	'Mozilla/5.0 (iPhone; CPU iPhone OS 18_0 like Mac OS X)',
	'AppleWebKit/605.1.15 (KHTML, like Gecko)',
	'Version/18.0 Mobile/15E148 Safari/604.1',
].join(' ')

const baseHeaders: HttpHeaders = {
	accept: '*/*',
	'content-type': 'application/json',
	origin: PLAYHQ_ORIGIN,
	referer: `${PLAYHQ_ORIGIN}/`,
	'user-agent': USER_AGENT,
}

const searchQuery = `
query search($filter: SearchFilter!) {
  search(filter: $filter) {
    meta {
      page
      totalPages
      totalRecords
    }
    results {
      ... on Organisation {
        id
        routingCode
        name
        type
        logo {
          sizes {
            url
            dimensions {
              width
              height
            }
          }
        }
        tenant {
          slug
        }
      }
    }
  }
}
`

const discoverCompetitionsQuery = `
query discoverCompetitions($organisationID: ID!, $organisationCode: String!) {
  discoverCompetitions(organisationID: $organisationID) {
    id
    name
    seasons(organisationID: $organisationID) {
      id
      name
      startDate
      endDate
      status {
        name
        value
      }
    }
    organisation {
      id
      name
    }
  }
  discoverOrganisation(code: $organisationCode) {
    id
    type
    name
  }
  tenantConfiguration {
    label
    sport {
      name
    }
  }
}
`

const discoverOrganisationTeamsQuery = `
query discoverOrganisationTeams($seasonCode: String!, $seasonId: ID!, $organisationCode: String!, $organisationId: ID!) {
  discoverSeason(seasonID: $seasonCode) {
    id
    name
    competition {
      id
      name
      organisation {
        id
        name
      }
    }
    status {
      name
      value
    }
    grades {
      id
      name
    }
  }
  tenantConfiguration {
    label
    sport {
      name
    }
  }
  discoverTeams(filter: { seasonID: $seasonId, organisationID: $organisationId }) {
    id
    name
    gender {
      name
      value
    }
    ageGroup {
      name
      value
    }
    grade {
      id
      name
    }
  }
  discoverOrganisation(code: $organisationCode) {
    id
    name
    type
  }
}
`

const gradeAllRoundsQuery = `
query gradeAllRounds($gradeID: ID!) {
  discoverGradeFixture(gradeID: $gradeID) {
    id
    name
    games {
      id
      date
      status {
        name
        value
      }
      home {
        ... on DiscoverTeam {
          id
          name
        }
        ... on ProvisionalTeam {
          name
        }
      }
      away {
        ... on DiscoverTeam {
          id
          name
        }
        ... on ProvisionalTeam {
          name
        }
      }
      result {
        home {
          statistics {
            count
            type {
              value
            }
          }
          periods {
            period {
              shortName
              value
            }
            statistics {
              count
              type {
                value
              }
            }
          }
        }
        away {
          statistics {
            count
            type {
              value
            }
          }
          periods {
            period {
              shortName
              value
            }
            statistics {
              count
              type {
                value
              }
            }
          }
        }
      }
      allocation {
        dateTimeList {
          date
          time
        }
        time
        court {
          name
          venue {
            name
          }
        }
      }
    }
  }
}
`

const spectatorGameQuery = `
query game($id: ID!, $scope: PeriodScore) {
  game(id: $id) {
    id
    status
    result {
      home {
        statistics {
          type {
            type
            value
          }
          count
        }
        periods(scope: $scope) {
          period {
            label
            shortName
            value
          }
          statistics {
            type {
              type
              value
            }
            count
          }
        }
      }
      away {
        statistics {
          type {
            type
            value
          }
          count
        }
        periods(scope: $scope) {
          period {
            label
            shortName
            value
          }
          statistics {
            type {
              type
              value
            }
            count
          }
        }
      }
    }
  }
}
`

const gameCentreScoresQuery = `
query gameView($gameId: ID!) {
  discoverGame(gameID: $gameId) {
    id
    status {
      value
    }
    result {
      home {
        score
        periods {
          period {
            shortName
            value
          }
          statistics {
            type {
              value
            }
            count
          }
        }
      }
      away {
        score
        periods {
          period {
            shortName
            value
          }
          statistics {
            type {
              value
            }
            count
          }
        }
      }
    }
    statistics {
      home {
        periods {
          period {
            shortName
            value
          }
          statistics {
            type {
              value
            }
            count
          }
        }
      }
      away {
        periods {
          period {
            shortName
            value
          }
          statistics {
            type {
              value
            }
            count
          }
        }
      }
    }
  }
}
`

export function isPlayHqNativeTransportAvailable() {
	return Capacitor.isNativePlatform()
}

export async function searchPlayHqOrganisations(query: string): Promise<PlayHqOrganisationSearchResult[]> {
	const trimmed = query.trim()
	if (trimmed.length < 2) {
		return []
	}

	const data = await playHqGraphQl<{
		search?: {
			results?: unknown[]
		}
	}>(SEARCH_ENDPOINT, baseHeaders, {
		operationName: 'search',
		query: searchQuery,
		variables: {
			filter: {
				meta: {
					limit: 10,
					page: 1,
				},
				organisation: {
					query: trimmed,
					types: [ 'CLUB' ],
					sports: [ 'NETBALL' ],
				},
			},
		},
	})

	return asArray(data.search?.results)
		.map(result => {
			const item = asRecord(result)
			return {
				id: String(item.id ?? ''),
				routingCode: String(item.routingCode ?? ''),
				name: String(item.name ?? ''),
				type: String(item.type ?? ''),
				tenantSlug: optionalString(asRecord(item.tenant).slug),
				logoUrl: findLogoUrl(item.logo),
			}
		})
		.filter(result => result.routingCode && result.name)
}

export async function fetchPlayHqSeasonOptions(
	organisation: PlayHqOrganisationSearchResult,
): Promise<PlayHqSeasonOption[]> {
	const data = await playHqGraphQl<{
		discoverCompetitions?: unknown[]
		discoverOrganisation?: unknown
		tenantConfiguration?: unknown
	}>(API_ENDPOINT, apiHeaders(DEFAULT_TENANT), {
		operationName: 'discoverCompetitions',
		query: discoverCompetitionsQuery,
		variables: {
			organisationID: organisation.routingCode,
			organisationCode: organisation.routingCode,
		},
	})

	const tenant = optionalString(asRecord(data.tenantConfiguration).label) ?? DEFAULT_TENANT
	const discoveredOrganisation = asRecord(data.discoverOrganisation)
	const orgName = optionalString(discoveredOrganisation.name) ?? organisation.name

	const seasons = asArray(data.discoverCompetitions).flatMap(competition => {
		const competitionRecord = asRecord(competition)
		return asArray(competitionRecord.seasons).map(season => {
			const seasonRecord = asRecord(season)
			const status = asRecord(seasonRecord.status)
			return {
				competitionId: String(competitionRecord.id ?? ''),
				compName: String(competitionRecord.name ?? ''),
				logoUrl: organisation.logoUrl,
				orgName,
				organisationCode: organisation.routingCode,
				organisationId: organisation.id,
				seasonId: String(seasonRecord.id ?? ''),
				seasonName: String(seasonRecord.name ?? ''),
				status: optionalString(status.value),
				startDate: optionalString(seasonRecord.startDate),
				endDate: optionalString(seasonRecord.endDate),
				tenant,
				tenantSlug: organisation.tenantSlug,
			}
		})
	}).filter(season => season.competitionId && season.compName && season.seasonId && season.seasonName)

	return seasons.sort(compareSeasonOptions)
}

export async function fetchPlayHqTeamOptions(season: PlayHqSeasonOption): Promise<PlayHqTeamOption[]> {
	const data = await playHqGraphQl<{
		discoverTeams?: unknown[]
		discoverSeason?: unknown
		discoverOrganisation?: unknown
		tenantConfiguration?: unknown
	}>(API_ENDPOINT, apiHeaders(season.tenant), {
		operationName: 'discoverOrganisationTeams',
		query: discoverOrganisationTeamsQuery,
		variables: {
			seasonCode: season.seasonId,
			seasonId: season.seasonId,
			organisationCode: season.organisationCode,
			organisationId: season.organisationCode,
		},
	})

	const discoveredSeason = asRecord(data.discoverSeason)
	const competition = asRecord(discoveredSeason.competition)
	const organisation = asRecord(data.discoverOrganisation)
	const tenant = optionalString(asRecord(data.tenantConfiguration).label) ?? season.tenant
	const compName = optionalString(competition.name) ?? season.compName
	const orgName = optionalString(organisation.name) ?? season.orgName

	return asArray(data.discoverTeams)
		.map(team => {
			const teamRecord = asRecord(team)
			const grade = asRecord(teamRecord.grade)
			const ageGroup = asRecord(teamRecord.ageGroup)
			const gender = asRecord(teamRecord.gender)
			return {
				...season,
				compName,
				orgName,
				tenant,
				id: String(teamRecord.id ?? ''),
				teamName: String(teamRecord.name ?? ''),
				gradeId: String(grade.id ?? ''),
				gradeName: String(grade.name ?? ''),
				ageGroupName: optionalString(ageGroup.name),
				genderName: optionalString(gender.name),
			}
		})
		.filter(team => team.id && team.teamName && team.gradeId && team.gradeName)
		.sort((a, b) => a.teamName.localeCompare(b.teamName) || a.gradeName.localeCompare(b.gradeName))
}

export function playHqTeamToConnector(team: PlayHqTeamOption): PlayHqConnector {
	return {
		type: 'PlayHQ',
		id: team.id,
		params: {
			compName: team.compName,
			competitionId: team.competitionId,
			gradeId: team.gradeId,
			gradeName: team.gradeName,
			id: team.id,
			logoUrl: team.logoUrl,
			orgName: team.orgName,
			organisationCode: team.organisationCode,
			organisationId: team.organisationId,
			seasonId: team.seasonId,
			seasonName: team.seasonName,
			teamName: team.teamName,
			tenant: team.tenant,
			tenantSlug: team.tenantSlug,
		},
	}
}

export async function fetchPlayHqFixtureGames(connector: PlayHqConnector): Promise<PlayHqFixtureGame[]> {
	const { params } = connector
	const data = await playHqGraphQl<{
		discoverGradeFixture?: unknown[]
	}>(API_ENDPOINT, apiHeaders(params.tenant), {
		operationName: 'gradeAllRounds',
		query: gradeAllRoundsQuery,
		variables: {
			gradeID: params.gradeId,
		},
	})

	return asArray(data.discoverGradeFixture)
		.flatMap(round => normalizePlayHqRound(round, params.id))
}

export async function fetchPlayHqGameCentreScores(
	game: Pick<PlayHqFixtureGame, 'id' | 'amHome'>,
	tenant: string,
): Promise<PlayHqGameScores | null> {
	const data = await playHqGraphQl<{
		discoverGame?: unknown
	}>(API_ENDPOINT, apiHeaders(tenant), {
		operationName: 'gameView',
		query: gameCentreScoresQuery,
		variables: {
			gameId: game.id,
		},
	})

	if (!data.discoverGame) {
		return null
	}

	const gameRecord = asRecord(data.discoverGame)
	const result = asRecord(gameRecord.result)
	const homeResult = asRecord(result.home)
	const awayResult = asRecord(result.away)
	const statistics = asRecord(gameRecord.statistics)
	const homeStatistics = asRecord(statistics.home)
	const awayStatistics = asRecord(statistics.away)

	// The public game centre gets Portland-style period scores from
	// statistics.*.periods, while some PlayHQ responses put periods under result.*.
	const homePeriodScores = extractFirstPeriodScores(homeStatistics.periods, homeResult.periods)
	const awayPeriodScores = extractFirstPeriodScores(awayStatistics.periods, awayResult.periods)
	const homeScore = readScore(homeResult.score)
	const awayScore = readScore(awayResult.score)

	return {
		status: optionalString(asRecord(gameRecord.status).value),
		meScore: game.amHome ? homeScore : awayScore,
		themScore: game.amHome ? awayScore : homeScore,
		mePeriodScores: game.amHome ? homePeriodScores : awayPeriodScores,
		themPeriodScores: game.amHome ? awayPeriodScores : homePeriodScores,
	}
}

export async function fetchPlayHqSpectatorGameScores(
	game: Pick<PlayHqFixtureGame, 'id' | 'amHome'>,
	tenant: string,
): Promise<PlayHqGameScores | null> {
	const data = await playHqGraphQl<{
		game?: unknown
	}>(SPECTATOR_ENDPOINT, spectatorHeaders(tenant), {
		operationName: 'game',
		query: spectatorGameQuery,
		variables: {
			id: game.id,
			scope: 'BY_PERIOD',
		},
	})

	if (!data.game) return null
	const gameRecord = asRecord(data.game)
	const result = asRecord(gameRecord.result)
	const home = asRecord(result.home)
	const away = asRecord(result.away)
	const homeScore = findTotalScore(home.statistics)
	const awayScore = findTotalScore(away.statistics)
	const homePeriodScores = extractPeriodScores(home.periods)
	const awayPeriodScores = extractPeriodScores(away.periods)

	return {
		status: optionalString(gameRecord.status),
		meScore: game.amHome ? homeScore : awayScore,
		themScore: game.amHome ? awayScore : homeScore,
		mePeriodScores: game.amHome ? homePeriodScores : awayPeriodScores,
		themPeriodScores: game.amHome ? awayPeriodScores : homePeriodScores,
	}
}

export function isCompletedPlayHqStatus(status: string | undefined) {
	return [ 'FINAL', 'COMPLETE', 'COMPLETED', 'ENDED' ].includes(String(status ?? '').toUpperCase())
}

export function makeCumulativePlayHqResult(
	scores: PlayHqGameScores,
	numPeriods: number,
): Result | null {
	if (
		!scores.mePeriodScores ||
		!scores.themPeriodScores ||
		scores.mePeriodScores.length !== numPeriods ||
		scores.themPeriodScores.length !== numPeriods
	) {
		return null
	}

	const result: Result = {
		me: makeCumulativePeriods(scores.mePeriodScores),
		them: makeCumulativePeriods(scores.themPeriodScores),
	}

	const cumulativeResult: Result = {
		me: [ ...scores.mePeriodScores ],
		them: [ ...scores.themPeriodScores ],
	}

	// PlayHQ's tested endpoints send per-period increments, but the public API also
	// talks about "End of Period" scores. If a source ever sends cumulative values,
	// prefer the interpretation that agrees with the explicit final score.
	if (playHqResultMatchesFinalScore(result, scores, numPeriods)) {
		return result
	}
	return playHqResultMatchesFinalScore(cumulativeResult, scores, numPeriods) ? cumulativeResult : null
}

async function playHqGraphQl<T>(
	url: string,
	headers: HttpHeaders,
	body: GraphQlBody,
): Promise<T> {
	if (!isPlayHqNativeTransportAvailable()) {
		throw new Error('PlayHQ sync is available in the iOS and Android app.')
	}

	const options: HttpOptions = {
		method: 'POST',
		url,
		headers,
		data: body,
		connectTimeout: 15000,
		readTimeout: 20000,
		responseType: 'json',
	}
	const response = await CapacitorHttp.request(options)
	const envelope = response.data as GraphQlEnvelope<T> | undefined

	if (response.status < 200 || response.status >= 300) {
		throw new Error(`PlayHQ request failed with HTTP ${response.status}`)
	}

	if (envelope?.errors) {
		// Spectator has no scores for these games; this is a miss, not a temporary outage.
		const errors = asArray(envelope.errors)
		if (url === SPECTATOR_ENDPOINT && errors.length > 0 && errors.every(error =>
			/\bnot electronically scored\b/i.test(String(asRecord(error).message ?? ''))
		)) {
			return { game: null } as T
		}
		throw new Error(`PlayHQ request returned GraphQL errors: ${JSON.stringify(envelope.errors)}`)
	}

	if (!envelope?.data) {
		throw new Error('PlayHQ request returned no data.')
	}

	return envelope.data
}

function apiHeaders(tenant: string): HttpHeaders {
	return {
		...baseHeaders,
		tenant,
	}
}

function spectatorHeaders(tenant: string): HttpHeaders {
	return {
		...baseHeaders,
		'x-phq-tenant': tenant,
	}
}

function normalizePlayHqRound(round: unknown, selectedTeamId: string): PlayHqFixtureGame[] {
	const roundRecord = asRecord(round)
	const roundName = String(roundRecord.name ?? '')

	return asArray(roundRecord.games)
		.map(game => normalizePlayHqGame(game, roundName, selectedTeamId))
		.filter((game): game is PlayHqFixtureGame => !!game)
}

function normalizePlayHqGame(
	game: unknown,
	roundName: string,
	selectedTeamId: string,
): PlayHqFixtureGame | null {
	const gameRecord = asRecord(game)
	const home = normalizePlayHqTeam(gameRecord.home)
	const away = normalizePlayHqTeam(gameRecord.away)
	const amHome = home.id === selectedTeamId
	const amAway = away.id === selectedTeamId

	if (!amHome && !amAway) {
		return null
	}

	const opponent = amHome ? away.name : home.name
	if (!opponent || opponent.trim().toLowerCase() === 'bye') {
		return null
	}

	const result = asRecord(gameRecord.result)
	const homeResult = asRecord(result.home)
	const awayResult = asRecord(result.away)
	const homeScore = findTotalScore(homeResult.statistics)
	const awayScore = findTotalScore(awayResult.statistics)
	const homePeriodScores = extractPeriodScores(homeResult.periods)
	const awayPeriodScores = extractPeriodScores(awayResult.periods)

	return {
		id: String(gameRecord.id ?? ''),
		date: parsePlayHqDateTime(gameRecord),
		opponent: opponent.trim(),
		round: normalizeRoundName(roundName),
		venue: normalizeVenue(gameRecord.allocation),
		amHome,
		status: optionalString(asRecord(gameRecord.status).value),
		meScore: amHome ? homeScore : awayScore,
		themScore: amHome ? awayScore : homeScore,
		mePeriodScores: amHome ? homePeriodScores : awayPeriodScores,
		themPeriodScores: amHome ? awayPeriodScores : homePeriodScores,
	}
}

function normalizePlayHqTeam(value: unknown) {
	const team = asRecord(value)
	return {
		id: optionalString(team.id),
		name: String(team.name ?? '').trim(),
	}
}

function parsePlayHqDateTime(game: Record<string, unknown>) {
	const allocation = asRecord(game.allocation)
	const firstDateTime = asRecord(asArray(allocation.dateTimeList)[0])
	const date = optionalString(firstDateTime.date) ?? optionalString(game.date) ?? ''
	const time = optionalString(firstDateTime.time) ?? optionalString(allocation.time) ?? '00:00:00'

	const dateMatch = date.match(/^(\d{4})-(\d{2})-(\d{2})/)
	const timeMatch = time.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?/)
	if (!dateMatch) {
		return Date.now()
	}

	const [, year, month, day] = dateMatch
	const [, hour = '0', minute = '0', second = '0'] = timeMatch ?? []
	return new Date(
		Number(year),
		Number(month) - 1,
		Number(day),
		Number(hour),
		Number(minute),
		Number(second),
	).getTime()
}

function normalizeRoundName(roundName: string) {
	return String(roundName || '').replace(/^Round\s+/i, '')
}

function normalizeVenue(allocation: unknown) {
	const court = asRecord(asRecord(allocation).court)
	const venue = asRecord(court.venue)
	const venueName = optionalString(venue.name)
	const courtName = optionalString(court.name)

	if (venueName && courtName && !venueName.toLowerCase().includes(courtName.toLowerCase())) {
		return `${venueName} - ${courtName}`
	}

	return venueName ?? courtName ?? ''
}

function findTotalScore(statistics: unknown): number | null {
	const total = asArray(statistics).find(statistic => {
		const item = asRecord(statistic)
		return asRecord(item.type).value === 'TOTAL_SCORE'
	})
	const count = total ? asRecord(total).count : null
	return readScore(count)
}

function extractPeriodScores(periods: unknown): number[] {
	// PlayHQ can return each side's periods in a different, non-chronological order.
	// Reject incomplete or unfamiliar sequences instead of assigning them to guessed quarters.
	const orders = [
		[ 'FIRST_QTR', 'SECOND_QTR', 'THIRD_QTR', 'FOURTH_QTR' ],
		[ 'FIRST_HALF', 'SECOND_HALF' ],
	]
	const entries = asArray(periods).map(value => {
		const entry = asRecord(value)
		return {
			period: asRecord(entry.period).value,
			score: findTotalScore(entry.statistics),
		}
	})
	const order = orders.find(sequence => sequence.length === entries.length &&
		sequence.every(period => entries.filter(entry => entry.period === period).length === 1))
	if (!order || entries.some(entry => entry.score === null || entry.score < 0)) {
		return []
	}
	return order.map(period => entries.find(entry => entry.period === period)!.score as number)
}

function extractFirstPeriodScores(...periodSources: unknown[]): number[] {
	for (const source of periodSources) {
		const scores = extractPeriodScores(source)
		if (scores.length) {
			return scores
		}
	}

	return []
}

function makeCumulativePeriods(periodScores: number[]) {
	let total = 0
	return periodScores.map(score => {
		total += score
		return total
	})
}

function playHqResultMatchesFinalScore(result: Result, scores: PlayHqGameScores, numPeriods: number) {
	return scoreMatchesFinal(result.me[numPeriods - 1], scores.meScore) &&
		scoreMatchesFinal(result.them[numPeriods - 1], scores.themScore)
}

function scoreMatchesFinal(value: unknown, finalScore: number | null | undefined) {
	return typeof finalScore !== 'number' || value === finalScore
}

function readScore(value: unknown) {
	if (typeof value === 'number' && Number.isFinite(value)) {
		return value
	}

	if (typeof value === 'string' && value.trim()) {
		const numericValue = Number(value)
		return Number.isFinite(numericValue) ? numericValue : null
	}

	return null
}

function findLogoUrl(logo: unknown) {
	const sizes = asArray(asRecord(logo).sizes)
		.map(size => {
			const sizeRecord = asRecord(size)
			const dimensions = asRecord(sizeRecord.dimensions)
			return {
				url: optionalString(sizeRecord.url),
				width: Number(dimensions.width ?? 0),
			}
		})
		.filter(size => size.url)
		.sort((a, b) => b.width - a.width)

	return sizes[0]?.url
}

function compareSeasonOptions(a: PlayHqSeasonOption, b: PlayHqSeasonOption) {
	const statusRank = (status?: string) => {
		switch (String(status ?? '').toUpperCase()) {
			case 'ACTIVE':
				return 0
			case 'UPCOMING':
				return 1
			case 'COMPLETED':
				return 2
			default:
				return 3
		}
	}

	return statusRank(a.status) - statusRank(b.status)
		|| b.seasonName.localeCompare(a.seasonName)
		|| a.compName.localeCompare(b.compName)
}

function optionalString(value: unknown) {
	return typeof value === 'string' && value.trim() ? value.trim() : undefined
}

function asRecord(value: unknown): Record<string, unknown> {
	return value && typeof value === 'object' ? value as Record<string, unknown> : {}
}

function asArray(value: unknown): unknown[] {
	return Array.isArray(value) ? value : []
}
